import { List, OrderedMap, Record } from 'immutable';
import { Utils, C } from '@kineticdata/bundle-common';
const { noPayload, withPayload } = Utils;
const ns = Utils.namespaceBuilder('services/settings/appendices');

export const types = {
  FETCH_APPENDICES_REQUEST: ns('FETCH_APPENDICES_REQUEST'),
  FETCH_APPENDICES_SUCCESS: ns('FETCH_APPENDICES_SUCCESS'),
  FETCH_APPENDICES_FAILURE: ns('FETCH_APPENDICES_FAILURE'),

  FETCH_APPENDIX_REQUEST: ns('FETCH_APPENDIX_REQUEST'),
  FETCH_APPENDIX_SUCCESS: ns('FETCH_APPENDIX_SUCCESS'),
  FETCH_APPENDIX_FAILURE: ns('FETCH_APPENDIX_FAILURE'),

  FETCH_APPENDIX_REVISION_REQUEST: ns('FETCH_APPENDIX_REVISION_REQUEST'),
  FETCH_APPENDIX_REVISION_SUCCESS: ns('FETCH_APPENDIX_REVISION_SUCCESS'),
  FETCH_APPENDIX_REVISION_FAILURE: ns('FETCH_APPENDIX_REVISION_FAILURE'),
  RESET_APPENDIX_REVISION_REQUEST: ns('RESET_APPENDIX_REVISION_REQUEST'),

  CREATE_APPENDIX_REQUEST: ns('CREATE_APPENDIX_REQUEST'),
  CLONE_APPENDIX_REQUEST: ns('CLONE_APPENDIX_REQUEST'),
  EXPIRE_APPENDIX_REQUEST: ns('EXPIRE_APPENDIX_REQUEST'),
  SAVE_APPENDIX_REVISION_REQUEST: ns('SAVE_APPENDIX_REVISION_REQUEST'),
  SUBMIT_APPENDIX_REVISION_REQUEST: ns('SUBMIT_APPENDIX_REVISION_REQUEST'),
  DELETE_APPENDIX_REVISION_REQUEST: ns('DELETE_APPENDIX_REVISION_REQUEST'),
  LOCK_APPENDIX_REVISION_REQUEST: ns('LOCK_APPENDIX_REVISION_REQUEST'),

  APPENDIX_ACTION_COMPLETE: ns('APPENDIX_ACTION_COMPLETE'),
};

export const actions = {
  fetchAppendicesRequest: noPayload(types.FETCH_APPENDICES_REQUEST),
  fetchAppendicesSuccess: withPayload(types.FETCH_APPENDICES_SUCCESS),
  fetchAppendicesFailure: withPayload(types.FETCH_APPENDICES_FAILURE),

  fetchAppendixRequest: withPayload(types.FETCH_APPENDIX_REQUEST),
  fetchAppendixSuccess: withPayload(types.FETCH_APPENDIX_SUCCESS),
  fetchAppendixFailure: withPayload(types.FETCH_APPENDIX_FAILURE),

  fetchAppendixRevisionRequest: withPayload(
    types.FETCH_APPENDIX_REVISION_REQUEST,
    'revisionId',
    'appendixId',
  ),
  fetchAppendixRevisionSuccess: withPayload(
    types.FETCH_APPENDIX_REVISION_SUCCESS,
  ),
  fetchAppendixRevisionFailure: withPayload(
    types.FETCH_APPENDIX_REVISION_FAILURE,
  ),
  resetAppendixRevisionRequest: withPayload(
    types.RESET_APPENDIX_REVISION_REQUEST,
  ),

  createAppendixRequest: withPayload(types.CREATE_APPENDIX_REQUEST),
  cloneAppendixRequest: withPayload(types.CLONE_APPENDIX_REQUEST),
  expireAppendixRequest: withPayload(types.EXPIRE_APPENDIX_REQUEST),
  saveAppendixRevisionRequest: withPayload(
    types.SAVE_APPENDIX_REVISION_REQUEST,
  ),
  submitAppendixRevisionRequest: withPayload(
    types.SUBMIT_APPENDIX_REVISION_REQUEST,
  ),
  deleteAppendixRevisionRequest: withPayload(
    types.DELETE_APPENDIX_REVISION_REQUEST,
  ),
  lockAppendixRevisionRequest: withPayload(
    types.LOCK_APPENDIX_REVISION_REQUEST,
  ),

  appendixActionComplete: withPayload(types.APPENDIX_ACTION_COMPLETE),
};

export const State = Record({
  data: OrderedMap(),
  dataList: List(),
  dataInit: false,
  dataLoading: false,
  dataError: null,

  current: null,
  currentLoading: false,
  currentError: null,

  revision: null,
  revisionLoading: false,
  revisionError: null,

  actionsPending: 0,
});

const updateDataList = state =>
  state.set(
    'dataList',
    state.data
      .map(l => l.find(p => p.values['Status'] === 'Active') || l.first())
      .toList(),
  );

export const reducer = (state = State(), { type, payload }) => {
  switch (type) {
    case types.FETCH_APPENDICES_REQUEST:
      return state.set('dataError', null).set('dataLoading', true);
    case types.FETCH_APPENDICES_SUCCESS:
      return (
        state
          // Group appendices by Appendix Id, sort each group by updatedAt:DESC,
          // and sort the map alphabetically.
          // TODO allow custom sort order
          .set(
            'data',
            List(payload)
              .groupBy(p => p.values[C.FIELD_APPENDIX_ID])
              .toOrderedMap()
              .map(l => l.sortBy(p => p.updatedAt).reverse())
              .sortBy(l => l.first().values['Name'].toLowerCase()),
          )
          .set('dataInit', true)
          .set('dataLoading', false)
          // Create a list of all current portofolios
          .update(updateDataList)
      );
    case types.FETCH_APPENDICES_FAILURE:
      return state.set('dataError', payload).set('dataLoading', false);

    case types.FETCH_APPENDIX_REQUEST:
      return state
        .update(
          'current',
          current =>
            // If appendixId changed, set new current appendix object
            !current || current.values[C.FIELD_APPENDIX_ID] !== payload
              ? state.dataList.find(
                  p => p.values[C.FIELD_APPENDIX_ID] === payload,
                )
              : current,
        )
        .set('currentError', null)
        .set('currentLoading', true);
    case types.FETCH_APPENDIX_SUCCESS:
      return (
        state
          .set('current', payload.record)
          .set('currentLoading', false)
          // Update the list of protfolios for the current id
          .update('data', data =>
            data.set(
              payload.appendixId,
              List(payload.list)
                .sortBy(p => p.updatedAt)
                .reverse(),
            ),
          )
          // Update list of all current portofolios with latest data
          .update(updateDataList)
      );
    case types.FETCH_APPENDIX_FAILURE:
      return state.set('currentError', payload).set('currentLoading', false);

    case types.FETCH_APPENDIX_REVISION_REQUEST:
      return state
        .update(
          'revision',
          p =>
            // If revisionId changed, clear revision appendix object
            !p || p.id !== payload.revisionId ? null : p,
        )
        .set('revisionError', null)
        .set('revisionLoading', true);
    case types.FETCH_APPENDIX_REVISION_SUCCESS:
      return (
        state
          .set('revision', payload)
          .set('revisionLoading', false)
          // Update the revision record in the list of appendices
          .updateIn(
            ['data', payload.values[C.FIELD_APPENDIX_ID]],
            list =>
              list
                ? list
                    .map(
                      p =>
                        p.id === payload.id
                          ? // Modify the revision to only have the necessary values
                            // when adding to data list to prevent the redux store
                            // from having a ton of data due to all the Content.
                            {
                              ...payload,
                              values: C.APPENDIX_PRIMARY_FIELDS.reduce(
                                (vals, field) => ({
                                  ...vals,
                                  [field]: payload.values[field],
                                }),
                                {},
                              ),
                            }
                          : p,
                    )
                    .sortBy(p => p.updatedAt)
                    .reverse()
                : list,
          )
          // Update list of all current portofolios with latest data
          .update(updateDataList)
      );
    case types.FETCH_APPENDIX_REVISION_FAILURE:
      return state.set('revisionError', payload).set('revisionLoading', false);
    case types.RESET_APPENDIX_REVISION_REQUEST:
      // If the current revision matches the one we wantt o reset, clear revision state
      return state.getIn(['revision', 'id']) === payload
        ? state
            .set('revision', null)
            .set('revisionLoading', false)
            .set('revisionError', null)
        : state;

    case types.CREATE_APPENDIX_REQUEST:
    case types.CLONE_APPENDIX_REQUEST:
    case types.EXPIRE_APPENDIX_REQUEST:
    case types.SAVE_APPENDIX_REVISION_REQUEST:
    case types.SUBMIT_APPENDIX_REVISION_REQUEST:
    case types.DELETE_APPENDIX_REVISION_REQUEST:
    case types.LOCK_APPENDIX_REVISION_REQUEST:
      return state.update('actionsPending', a => a + 1);
    case types.APPENDIX_ACTION_COMPLETE:
      return state.update('actionsPending', a => a - 1);

    default:
      return state;
  }
};

export default reducer;

export const selectIsAppendixOwner = profile =>
  profile.isSMO || profile.isCatalogManager;
